
import api from './api';
import { UserAccount, Role } from '../types';
import { searchZohoEmployees } from './zoho';

/**
 * Map frontend Role (permissions system) to backend UserRole enum
 * Frontend roles match the permissions system: ADMIN, CEO, DFO, CP, FLIGHT_PLANNER, RESERVATIONS, ACCOUNTS, GROUND_OPS
 * Backend UserRole enum: SUPER_ADMIN, ADMIN, DFO, CP, PILOT, FIRST_OFFICER, ENGINEER, DISPATCHER, ACCOUNTANT, CUSTOMER_SERVICE, READONLY
 */
function mapFrontendRoleToBackend(frontendRole: Role): string {
  const roleMap: Record<Role, string> = {
    'ADMIN': 'ADMIN',
    'CEO': 'SUPER_ADMIN', // CEO maps to SUPER_ADMIN
    'DFO': 'DFO',
    'CP': 'CP',
    'PILOT': 'PILOT',
    'FIRST_OFFICER': 'FIRST_OFFICER',
    'FLIGHT_PLANNER': 'DISPATCHER', // Flight planner is dispatcher
    'RESERVATIONS': 'CUSTOMER_SERVICE', // Reservations is customer service
    'ACCOUNTS': 'ACCOUNTANT', // Accounts is accountant
    'GROUND_OPS': 'ENGINEER', // Ground ops is engineer
  };
  return roleMap[frontendRole] || 'READONLY';
}

/**
 * Map backend UserRole enum to frontend Role (permissions system)
 */
function mapBackendRoleToFrontend(backendRole: string): Role {
  const roleMap: Record<string, Role> = {
    'ADMIN': 'ADMIN',
    'SUPER_ADMIN': 'CEO', // SUPER_ADMIN maps to CEO
    'DFO': 'DFO',
    'CP': 'CP',
    'PILOT': 'PILOT',
    'FIRST_OFFICER': 'FIRST_OFFICER',
    'DISPATCHER': 'FLIGHT_PLANNER',
    'CUSTOMER_SERVICE': 'RESERVATIONS',
    'ACCOUNTANT': 'ACCOUNTS',
    'ENGINEER': 'GROUND_OPS',
  };
  return roleMap[backendRole] || 'RESERVATIONS'; // Default to RESERVATIONS (read-only equivalent)
}

const proxyAvatarUrl = (avatarUrl: string | undefined): string => {
  if (!avatarUrl) return '';
  if (!avatarUrl.startsWith('http')) return avatarUrl;
  const apiBase = import.meta.env.VITE_API_URL || '/meos/api';
  const jwt = localStorage.getItem('ma_jwt') || '';
  return `${apiBase}/auth/zoho-photo?url=${encodeURIComponent(avatarUrl)}${jwt ? `&jwt=${encodeURIComponent(jwt)}` : ''}`;
};

const parseSharedSignatureUsers = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.map(item => String(item || '').trim()).filter(Boolean);
  return String(value || '')
    .split(',')
    .map(token => token.trim())
    .filter(Boolean);
};

const parseInboundInboxKeys = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.map(item => String(item || '').trim().toLowerCase()).filter(Boolean);
  return String(value || '')
    .split(/[,\n;]/g)
    .map(token => token.trim().toLowerCase())
    .filter(Boolean);
};

export const userService = {
  /**
   * Fetch all users from the PostgreSQL registry
   */
  getAllUsers: async (): Promise<UserAccount[]> => {
    const response = await api.get('/users');
    // Proxy avatar URLs to add JWT auth for Zoho photo access
    // Map backend user fields to frontend UserAccount format
    return response.data.map((user: any) => {
      const photoUrl = user.photoUrl || user.avatar;
      // Map backend role to frontend role (permissions system)
      const frontendRole = mapBackendRoleToFrontend(user.role || 'READONLY');
      return {
        ...user,
        name: user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'Unknown',
        avatar: photoUrl ? proxyAvatarUrl(photoUrl) : null,
        photoUrl: photoUrl || null, // Also keep photoUrl for direct access
        emailSignatureSharedWith: parseSharedSignatureUsers((user as any).emailSignatureSharedWith),
        allowedInboundInboxKeys: parseInboundInboxKeys((user as any).allowedInboundInboxKeys),
        role: frontendRole, // Use frontend role (matching permissions system)
      };
    });
  },

  getInboundInboxOptions: async (): Promise<string[]> => {
    const response = await api.get('/users/inbox-options');
    return Array.isArray(response.data?.data?.options)
      ? response.data.data.options.map((item: any) => String(item || '').trim().toLowerCase()).filter(Boolean)
      : [];
  },

  /**
   * Search Zoho People Directory
   */
  searchZoho: async (query: string) => {
    return await searchZohoEmployees(query);
  },

  /**
   * Provision a new user in the database
   */
  createUser: async (user: Partial<UserAccount>): Promise<UserAccount> => {
    // Map frontend fields to backend schema
    // Frontend uses permissions system roles, need to map to backend UserRole enum
    const frontendRole = (user.role as Role) || 'RESERVATIONS';
    const backendRole = mapFrontendRoleToBackend(frontendRole);
    
    const payload: any = {
      email: user.email,
      firstName: (user as any).firstName,
      lastName: (user as any).lastName,
      phoneNumber: (user as any).phoneNumber,
      personalEmail: (user as any).personalEmail,
      emailSignature: (user as any).emailSignature,
      emailSignatureSharedWith: (user as any).emailSignatureSharedWith,
      allowedInboundInboxKeys: (user as any).allowedInboundInboxKeys,
      charterDiscountCap: (user as any).charterDiscountCap,
      scheduledDiscountCap: (user as any).scheduledDiscountCap,
      role: backendRole, // Map frontend role to backend role
      zohoId: (user as any).zohoId,
      // Map avatar to photoUrl - handle empty strings
      photoUrl: (user as any).photoUrl || ((user as any).avatar && (user as any).avatar.trim() !== '' ? (user as any).avatar : null) || null,
    };
    
    // Remove undefined and null fields (except photoUrl which can be null)
    Object.keys(payload).forEach(key => {
      if (key !== 'photoUrl' && payload[key] === undefined) {
        delete payload[key];
      }
    });
    
    console.log('[UserService] Creating user with frontend role:', frontendRole, 'mapped to backend role:', backendRole);
    const response = await api.post('/users', payload);
    
    // Map response back to frontend role
    const createdUser = response.data;
    return {
      ...createdUser,
      allowedInboundInboxKeys: parseInboundInboxKeys((createdUser as any).allowedInboundInboxKeys),
      role: mapBackendRoleToFrontend(createdUser.role || 'READONLY'),
    };
  },

  /**
   * Update user details or status
   */
  updateUser: async (id: string, updates: Partial<UserAccount>): Promise<UserAccount> => {
    // Map frontend role to backend role if role is being updated
    const payload: any = { ...updates };
    if (updates.role) {
      const frontendRole = updates.role as Role;
      payload.role = mapFrontendRoleToBackend(frontendRole);
      console.log('[UserService] Updating user role:', frontendRole, 'mapped to backend role:', payload.role);
    }
    if (Object.prototype.hasOwnProperty.call(updates, 'emailSignatureSharedWith')) {
      payload.emailSignatureSharedWith = Array.isArray((updates as any).emailSignatureSharedWith)
        ? (updates as any).emailSignatureSharedWith
        : [];
    }
    if (Object.prototype.hasOwnProperty.call(updates, 'allowedInboundInboxKeys')) {
      payload.allowedInboundInboxKeys = Array.isArray((updates as any).allowedInboundInboxKeys)
        ? (updates as any).allowedInboundInboxKeys
        : [];
    }
    
    const response = await api.patch(`/users/${id}`, payload);
    
    // Map response back to frontend role
    const updatedUser = response.data;
    return {
      ...updatedUser,
      emailSignatureSharedWith: parseSharedSignatureUsers((updatedUser as any).emailSignatureSharedWith),
      allowedInboundInboxKeys: parseInboundInboxKeys((updatedUser as any).allowedInboundInboxKeys),
      role: mapBackendRoleToFrontend(updatedUser.role || 'READONLY'),
    };
  },

  uploadSignatureImage: async (
    userId: string,
    payload: { fileName: string; mimeType: string; dataBase64: string }
  ): Promise<{ fileName: string; url: string; htmlSnippet: string }> => {
    const response = await api.post(`/users/${userId}/signature-image`, payload);
    return response?.data?.data || response?.data;
  },

  /**
   * Delete or hard-deactivate a user
   */
  deleteUser: async (id: string): Promise<void> => {
    await api.delete(`/users/${id}`);
  }
};
